import { BotonExportar } from '@/components/dominio/BotonExportar';
import { ComentarioSeccion } from '@/components/dominio/ComentarioSeccion';
import { KpiCard } from '@/components/dominio/KpiCard';
import { MontoCRC } from '@/components/dominio/MontoCRC';
import { SelectorPeriodo } from '@/components/dominio/SelectorPeriodo';
import { SortableHeader, useSortColumn } from '@/components/dominio/SortableHeader';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { usePeriodo } from '@/hooks/usePeriodo';
import { useResumen } from '@/hooks/useUtilidades';
import { formatearPeriodo, mesAnclaPeriodo } from '@adelante/shared';
import { ChevronDown, ChevronRight, Loader2 } from 'lucide-react';
import { Fragment, useMemo, useRef, useState } from 'react';
import { Bar, BarChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';

function extraerBloque(lote: string): string {
  const idx = lote.indexOf('.');
  return idx > 0 ? lote.slice(0, idx) : lote;
}

const NOMBRES_COMPONENTES: Record<string, string> = {
  inversion_casas: 'Inv. Casas',
  inversion_proyectos: 'Inv. Proyectos',
  otros: 'Otros',
  salida_quinta: 'Quinta',
  salida_homes: 'Homes',
  salida_socios: 'Socios',
  credito_clientes: 'Créd. Clientes',
  credito_colaboradores: 'Créd. Colabs.',
  compra_maquinaria: 'Maquinaria',
};

function abreviarCRC(valor: number): string {
  if (Math.abs(valor) >= 1e9) return `₡${(valor / 1e9).toFixed(1)}bn`;
  if (Math.abs(valor) >= 1e6) return `₡${(valor / 1e6).toFixed(0)}M`;
  return `₡${valor.toLocaleString('es-CR')}`;
}

type FilaTipo = { tipo_movimiento: string; monto_total: number; cantidad_movimientos: number };
type GrupoLote = { bloque: string; sumaTotal: number };

export function ResumenUtilidadesPantalla() {
  const { periodo, setModo, setAnio, setMes, setTrimestre, setDesde, setHasta } = usePeriodo();
  const [expandidosLote, setExpandidosLote] = useState<Set<string>>(new Set());
  const { data, isLoading, error } = useResumen(periodo);
  const refContenido = useRef<HTMLDivElement>(null);

  const etiquetaPeriodo = formatearPeriodo(periodo);
  const ancla = mesAnclaPeriodo(periodo);

  const sortTipo = useSortColumn<FilaTipo>(null);
  const sortLote = useSortColumn<GrupoLote>(null);

  function toggleBloque(bloque: string) {
    setExpandidosLote((prev) => {
      const next = new Set(prev);
      if (next.has(bloque)) next.delete(bloque);
      else next.add(bloque);
      return next;
    });
  }

  const gruposLote = useMemo(() => {
    if (!data) return [];
    const map = new Map<string, typeof data.movimientosPorLote>();
    for (const fila of data.movimientosPorLote) {
      const bloque = extraerBloque(fila.lote);
      if (!map.has(bloque)) map.set(bloque, []);
      map.get(bloque)!.push(fila);
    }
    return Array.from(map.entries()).map(([bloque, lotes]) => ({
      bloque,
      lotes,
      sumaTotal: lotes.reduce((acc, l) => acc + Number(l.monto), 0),
    }));
  }, [data]);

  const distribucionOrdenada = useMemo(() => {
    if (!data) return [];
    return sortTipo.sortData(
      data.distribucionPorTipo.map((f) => ({
        tipo_movimiento: f.tipo_movimiento,
        monto_total: Number(f.monto_total),
        cantidad_movimientos: f.cantidad_movimientos,
      })),
    );
  }, [data, sortTipo.sortData]);

  const gruposOrdenados = useMemo(
    () => sortLote.sortData(gruposLote),
    [gruposLote, sortLote.sortData],
  );

  // Hooks de exportación PDF: expandir todos los bloques con múltiples lotes
  // antes de capturar, para que el detalle aparezca en el PDF.
  const expandidosPreviosRef = useRef<Set<string> | null>(null);
  function antesDeExportarPDF() {
    expandidosPreviosRef.current = expandidosLote;
    const todos = new Set<string>();
    for (const g of gruposOrdenados) {
      if (g.lotes.length > 1) todos.add(g.bloque);
    }
    setExpandidosLote(todos);
  }
  function despuesDeExportarPDF() {
    if (expandidosPreviosRef.current) {
      setExpandidosLote(expandidosPreviosRef.current);
      expandidosPreviosRef.current = null;
    }
  }

  const datosGastada = data
    ? Object.entries(data.componentesGastada)
        .map(([k, v]) => ({ nombre: NOMBRES_COMPONENTES[k] ?? k, monto: v }))
        .filter((d) => d.monto > 0)
        .sort((a, b) => b.monto - a.monto)
    : [];

  const datosDistribucion = data
    ? data.distribucionPorTipo.map((f) => ({
        nombre: f.tipo_movimiento,
        monto: Number(f.monto_total),
      }))
    : [];

  return (
    <div ref={refContenido} className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Resumen de Utilidades</h1>
          <p className="mt-1 text-sm text-muted-foreground">{etiquetaPeriodo}</p>
        </div>
        <div data-pdf-ignore className="flex flex-wrap items-start gap-3">
          <SelectorPeriodo
            periodo={periodo}
            onSetModo={setModo}
            onSetAnio={setAnio}
            onSetMes={setMes}
            onSetTrimestre={setTrimestre}
            onSetDesde={setDesde}
            onSetHasta={setHasta}
          />
          <BotonExportar
            titulo="Resumen de Utilidades"
            subtitulo={etiquetaPeriodo}
            refContenido={refContenido}
            disabled={!data}
            onAntesExportarPDF={antesDeExportarPDF}
            onDespuesExportarPDF={despuesDeExportarPDF}
            tablas={
              data
                ? [
                    {
                      nombre: 'Distribución por tipo',
                      encabezados: ['Tipo de movimiento', 'Monto (₡)', 'Movimientos'],
                      filas: data.distribucionPorTipo.map((f) => [
                        f.tipo_movimiento,
                        Number(f.monto_total),
                        f.cantidad_movimientos,
                      ]),
                    },
                    {
                      nombre: 'Por lote',
                      encabezados: ['Lote', 'Monto (₡)'],
                      filas: data.movimientosPorLote.map((f) => [f.lote, Number(f.monto)]),
                    },
                  ]
                : []
            }
          />
        </div>
      </div>

      {isLoading && (
        <div className="flex items-center gap-2 text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          Cargando…
        </div>
      )}

      {error && (
        <Card className="border-destructive">
          <CardContent className="pt-6 text-destructive">{error.message}</CardContent>
        </Card>
      )}

      {data && (
        <>
          {/* Ingresos del período (contexto de cuánto entró al banco vs neto AD) */}
          <div className="space-y-2">
            <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
              Ingresos del período
            </p>
            <div className="grid grid-cols-2 gap-4">
              <KpiCard titulo="Ingreso Bruto" monto={data.ecuacionPrincipal.ingreso_bruto} />
              <KpiCard
                titulo="Ingreso Neto AD"
                monto={data.ecuacionPrincipal.ingreso_neto_ad}
                destacado
              />
            </div>
          </div>

          {/* Ecuación de utilidad (pool histórico + cálculo) */}
          <div className="space-y-2">
            <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
              Utilidad
            </p>
            <div className="grid grid-cols-2 gap-4 md:grid-cols-5">
              <KpiCard
                titulo="Utilidad Ingresada"
                monto={data.ecuacionPrincipal.utilidad_ingresada}
              />
              <KpiCard
                titulo="Devolución Utilidad"
                monto={data.ecuacionPrincipal.devolucion_utilidad}
              />
              <KpiCard titulo="Total" monto={data.ecuacionPrincipal.utilidad_total} />
              <KpiCard titulo="Gastada" monto={data.ecuacionPrincipal.utilidad_gastada} />
              <KpiCard titulo="Neta" monto={data.ecuacionPrincipal.utilidad_neta} destacado />
            </div>
          </div>

          <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle className="text-sm">Utilidad Gastada</CardTitle>
              </CardHeader>
              <CardContent>
                {datosGastada.length > 0 ? (
                  <ResponsiveContainer width="100%" height={240}>
                    <BarChart
                      data={datosGastada}
                      layout="vertical"
                      margin={{ left: 8, right: 24, top: 4, bottom: 4 }}
                    >
                      <XAxis
                        type="number"
                        tickFormatter={abreviarCRC}
                        tick={{ fontSize: 11 }}
                        axisLine={false}
                        tickLine={false}
                      />
                      <YAxis
                        type="category"
                        dataKey="nombre"
                        width={90}
                        tick={{ fontSize: 11 }}
                        axisLine={false}
                        tickLine={false}
                      />
                      <Tooltip
                        formatter={(v) => [abreviarCRC(Number(v)), 'Monto']}
                        cursor={{ fill: 'hsl(var(--muted))' }}
                      />
                      <Bar dataKey="monto" fill="#2563eb" radius={[0, 4, 4, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <p className="py-8 text-center text-sm text-muted-foreground">Sin datos</p>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-sm">Distribución por tipo</CardTitle>
              </CardHeader>
              <CardContent>
                {datosDistribucion.length > 0 ? (
                  <ResponsiveContainer width="100%" height={240}>
                    <BarChart
                      data={datosDistribucion}
                      layout="vertical"
                      margin={{ left: 8, right: 24, top: 4, bottom: 4 }}
                    >
                      <XAxis
                        type="number"
                        tickFormatter={abreviarCRC}
                        tick={{ fontSize: 11 }}
                        axisLine={false}
                        tickLine={false}
                      />
                      <YAxis
                        type="category"
                        dataKey="nombre"
                        width={120}
                        tick={{ fontSize: 11 }}
                        axisLine={false}
                        tickLine={false}
                      />
                      <Tooltip
                        formatter={(v) => [abreviarCRC(Number(v)), 'Monto']}
                        cursor={{ fill: 'hsl(var(--muted))' }}
                      />
                      <Bar dataKey="monto" fill="#7c3aed" radius={[0, 4, 4, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <p className="py-8 text-center text-sm text-muted-foreground">Sin datos</p>
                )}
              </CardContent>
            </Card>
          </div>

          <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle className="text-sm">Distribución por tipo</CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <div className="max-h-64 overflow-y-auto px-6 pb-4">
                  <Table>
                    <TableHeader className="sticky top-0 bg-card">
                      <TableRow>
                        <TableHead>
                          <SortableHeader
                            label="Tipo"
                            sortKey="tipo_movimiento"
                            activeSortKey={sortTipo.sortKey}
                            direction={sortTipo.direction}
                            onSort={sortTipo.handleSort}
                          />
                        </TableHead>
                        <TableHead className="text-right">
                          <SortableHeader
                            label="Monto"
                            sortKey="monto_total"
                            activeSortKey={sortTipo.sortKey}
                            direction={sortTipo.direction}
                            onSort={sortTipo.handleSort}
                            align="right"
                          />
                        </TableHead>
                        <TableHead className="text-right">
                          <SortableHeader
                            label="Movimientos"
                            sortKey="cantidad_movimientos"
                            activeSortKey={sortTipo.sortKey}
                            direction={sortTipo.direction}
                            onSort={sortTipo.handleSort}
                            align="right"
                          />
                        </TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {distribucionOrdenada.map((fila) => (
                        <TableRow key={fila.tipo_movimiento}>
                          <TableCell>{fila.tipo_movimiento}</TableCell>
                          <TableCell className="text-right">
                            <MontoCRC monto={fila.monto_total} />
                          </TableCell>
                          <TableCell className="text-right text-muted-foreground">
                            {fila.cantidad_movimientos}
                          </TableCell>
                        </TableRow>
                      ))}
                      {distribucionOrdenada.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={3} className="text-center text-muted-foreground">
                            Sin movimientos
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-sm">Movimientos por lote</CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <div className="max-h-64 overflow-y-auto px-6 pb-4">
                  <Table>
                    <TableHeader className="sticky top-0 bg-card">
                      <TableRow>
                        <TableHead className="w-6" />
                        <TableHead>
                          <SortableHeader
                            label="Bloque / Lote"
                            sortKey="bloque"
                            activeSortKey={sortLote.sortKey}
                            direction={sortLote.direction}
                            onSort={sortLote.handleSort}
                          />
                        </TableHead>
                        <TableHead className="text-right">
                          <SortableHeader
                            label="Monto"
                            sortKey="sumaTotal"
                            activeSortKey={sortLote.sortKey}
                            direction={sortLote.direction}
                            onSort={sortLote.handleSort}
                            align="right"
                          />
                        </TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {gruposOrdenados.map((grupo) => {
                        const tieneMultiples = grupo.lotes.length > 1;
                        const expandido = expandidosLote.has(grupo.bloque);
                        return (
                          <Fragment key={grupo.bloque}>
                            <TableRow
                              className={
                                tieneMultiples
                                  ? 'cursor-pointer select-none hover:bg-muted/40'
                                  : undefined
                              }
                              onClick={
                                tieneMultiples ? () => toggleBloque(grupo.bloque) : undefined
                              }
                            >
                              <TableCell className="text-muted-foreground">
                                {tieneMultiples &&
                                  (expandido ? (
                                    <ChevronDown className="h-3.5 w-3.5" />
                                  ) : (
                                    <ChevronRight className="h-3.5 w-3.5" />
                                  ))}
                              </TableCell>
                              <TableCell className={tieneMultiples ? 'font-semibold' : undefined}>
                                {tieneMultiples ? `Bloque ${grupo.bloque}` : grupo.lotes[0]!.lote}
                              </TableCell>
                              <TableCell className="text-right font-semibold">
                                <MontoCRC monto={grupo.sumaTotal} />
                              </TableCell>
                            </TableRow>
                            {tieneMultiples &&
                              expandido &&
                              grupo.lotes.map((fila) => (
                                <TableRow key={fila.lote} className="bg-muted/20">
                                  <TableCell className="text-center text-xs text-muted-foreground">
                                    ↳
                                  </TableCell>
                                  <TableCell className="pl-4 text-sm">{fila.lote}</TableCell>
                                  <TableCell className="text-right text-sm">
                                    <MontoCRC monto={Number(fila.monto)} />
                                  </TableCell>
                                </TableRow>
                              ))}
                          </Fragment>
                        );
                      })}
                      {gruposOrdenados.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={3} className="text-center text-muted-foreground">
                            Sin datos por lote
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          </div>
        </>
      )}

      <ComentarioSeccion anio={ancla.anio} mes={ancla.mes} scope="seccion" seccionId="resumen" />
    </div>
  );
}
